import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from 'components/AppIcon';

const PortfolioAnalytics = ({ holdings, totalValue }) => {
  const pieData = holdings.map((holding) => ({
    name: holding.symbol,
    value: holding.allocation,
    color: getColorForAsset(holding.symbol),
    currentValue: holding.currentValue
  }));

  function getColorForAsset(symbol) {
    const colors = {
      'BTC': '#F7931A',
      'ETH': '#627EEA',
      'ADA': '#0033AD',
      'SOL': '#9945FF',
      'DOT': '#E6007A'
    };
    return colors[symbol] || 'var(--color-primary)';
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface border border-border rounded-trading p-3 shadow-trading-modal">
          <p className="font-body-medium text-sm text-text-primary mb-1">{data.name}</p>
          <p className="font-data text-sm text-text-secondary">
            ${data.currentValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="font-data text-sm text-text-secondary">
            {data.value.toFixed(1)}% of portfolio
          </p>
        </div>
      );
    }
    return null;
  };

  const calculateRiskMetrics = () => {
    const profitableAssets = holdings.filter(h => h.unrealizedPnL > 0).length;
    const totalAssets = holdings.length;
    const diversificationScore = totalAssets >= 5 ? 'High' : totalAssets >= 3 ? 'Medium' : 'Low';
    const riskLevel = holdings[0]?.allocation > 80 ? 'High' : holdings[0]?.allocation > 60 ? 'Medium' : 'Low';
    
    return {
      profitableAssets,
      totalAssets,
      diversificationScore,
      riskLevel,
      profitablePercentage: ((profitableAssets / totalAssets) * 100).toFixed(0)
    };
  };

  const metrics = calculateRiskMetrics();

  return (
    <div className="space-y-6">
      {/* Asset Allocation Chart */}
      <div className="bg-surface border border-border rounded-trading p-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
          Asset Allocation
        </h3>
        
        <div className="h-48 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-2">
          {pieData.map((item) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="font-body-medium text-sm text-text-primary">{item.name}</span>
              </div>
              <span className="font-data text-sm text-text-secondary">
                {item.value.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Portfolio Analytics */}
      <div className="bg-surface border border-border rounded-trading p-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
          Portfolio Analytics
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="Target" size={16} className="text-text-secondary" />
              <span className="font-body-medium text-sm text-text-secondary">Diversification</span>
            </div>
            <span className={`font-body-medium text-sm px-2 py-1 rounded-trading ${
              metrics.diversificationScore === 'High' ? 'bg-success-50 text-success' :
              metrics.diversificationScore === 'Medium'? 'bg-warning-50 text-warning' : 'bg-error-50 text-error'
            }`}>
              {metrics.diversificationScore}
            </span>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-text-secondary" />
              <span className="font-body-medium text-sm text-text-secondary">Risk Level</span>
            </div>
            <span className={`font-body-medium text-sm px-2 py-1 rounded-trading ${
              metrics.riskLevel === 'Low' ? 'bg-success-50 text-success' :
              metrics.riskLevel === 'Medium'? 'bg-warning-50 text-warning' : 'bg-error-50 text-error'
            }`}>
              {metrics.riskLevel}
            </span>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingUp" size={16} className="text-text-secondary" />
              <span className="font-body-medium text-sm text-text-secondary">Profitable Assets</span>
            </div>
            <span className="font-data text-sm text-text-primary">
              {metrics.profitableAssets}/{metrics.totalAssets} ({metrics.profitablePercentage}%)
            </span>
          </div>

          <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-2">
              <Icon name="PieChart" size={16} className="text-text-secondary" />
              <span className="font-body-medium text-sm text-text-secondary">Total Assets</span>
            </div>
            <span className="font-data text-sm text-text-primary">
              {metrics.totalAssets}
            </span>
          </div>
        </div>
      </div>

      {/* Performance Comparison */}
      <div className="bg-surface border border-border rounded-trading p-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
          Market Comparison
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-body-medium text-sm text-text-secondary">Your Portfolio</span>
            <span className="font-data text-sm text-success">+2.65%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-body-medium text-sm text-text-secondary">Bitcoin (BTC)</span>
            <span className="font-data text-sm text-success">+2.30%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-body-medium text-sm text-text-secondary">Crypto Market</span>
            <span className="font-data text-sm text-success">+1.85%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-body-medium text-sm text-text-secondary">S&P 500</span>
            <span className="font-data text-sm text-error">-0.45%</span>
          </div>
        </div>

        <div className="mt-4 p-3 bg-success-50 rounded-trading">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="font-body-medium text-sm text-success">
              Outperforming market by 0.80%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioAnalytics;